import React, { useEffect, useRef } from 'react';
import { useInput, FieldTitle, InputHelperText, useResourceContext } from 'react-admin';
import { useFormState } from 'react-final-form';
import { KeyboardTimePicker } from '@material-ui/pickers';
import { AccessTime } from '@material-ui/icons';
import moment from 'moment-timezone';

const defaultTimeOptions = {
  variant: 'inline',
  ampm: false,
  minutesStep: 5,
  autoOk: true,
  placeholder: 'HH:mm',
  format: 'HH:mm',
};

const getTimeOptions = (custom = {}) => ({
  ...defaultTimeOptions,
  ...custom,
});

const TimeInput = ({ defaultTimezone, ...props }) => {
  const { values: { timezone } = {} } = useFormState();
  const inputRef = useRef(null);

  const {
    id,
    input: { name, value, onChange, onFocus, onBlur, ...input },
    meta: { active, touched, error, submitError },
    isRequired
  } = useInput(props);

  const {
    options = {},
    label,
    source,
    helperText,
    className,
    variant,
    margin = 'dense',
  } = props;

  const resource = useResourceContext(props);
  const allOptions = getTimeOptions(options);

  const _timezone = timezone || defaultTimezone || 'UTC';

  const parsedValue = moment(value).isValid()
    ? moment.tz(value, _timezone)
    : null;

  const handleTimeChange = (time) => {
    if (!time || !moment(time).isValid()) {
      return;
    }

    const formatted = time.format(allOptions.format);
    const iso = moment.tz(formatted, allOptions.format, _timezone).toISOString();
    onChange(iso);
  };

  const handleBlur = (e) => {
    onBlur(e);

    const rawValue = e.target.value;

    if (rawValue.includes('_')) {
      const fixed = rawValue.replace(/_/g, '0');

      if (moment(fixed, allOptions.format, true).isValid()) {
        const zoned = moment.tz(fixed, allOptions.format, _timezone);
        onChange(zoned.toISOString());
      }
    } else if (!moment(rawValue, allOptions.format, true).isValid()) {
      onChange(null);
    }
  };


  const handleFocus = (e) => {
    onFocus(e);
  };

  useEffect(() => {
    if (active && inputRef.current) {
      inputRef.current.focus();
    }
  }, [active]);

  return (
    <KeyboardTimePicker
      {...props}
      id={id}
      name={name}
      value={parsedValue}
      onChange={handleTimeChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      inputVariant={variant}
      margin={margin}
      keyboardIcon={<AccessTime />}
      KeyboardButtonProps={{ tabIndex: -1, style: { padding: 0 } }}
      inputProps={{ ref: inputRef }}
      InputLabelProps={{
        shrink: active || !!parsedValue || !!inputRef.current?.value,
      }}
      label={
        <FieldTitle
          label={label}
          source={source}
          resource={resource}
          isRequired={isRequired}
        />
      }
      helperText={
        (touched && (error || submitError)) || helperText ? (
          <InputHelperText
            touched={touched}
            error={error || submitError}
            helperText={helperText}
          />
        ) : null
      }
      error={!!(touched && (error || submitError))}
      className={className}
      {...allOptions}
      {...input}
    />
  );
};

export default TimeInput;
